import { useEffect, useState } from "react";
import { useViewer } from "../../../../state/ViewerProvider";
import { useEditorStore } from "../../../../state/editor/useEditor";
import { MiniBtn } from "../../inspector/rows";

const errText = (e: unknown) =>
  e instanceof Error && e.message ? e.message : "Applying the variant failed.";

/**
 * glTF KHR_materials_variants (v14, wired for real since v31). Lists the
 * model's material variants (read at load) and applies one on click; gem meshes
 * are skipped so a ShaderMaterial never acquires a PBR material. Jewelry GLBs
 * rarely carry the extension, so the empty state is the common one.
 */
export function KhrVariants() {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const [variants, setVariants] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      r.listMaterialVariants()
        .then((v) => alive && setVariants(v))
        .catch(() => alive && setVariants([]));
    };
    load();
    const off = client.on("modelsChanged", load);
    return () => {
      alive = false;
      off();
    };
  }, [client, r]);

  /**
   * Applying a variant swaps the material of every mapped mesh, so the Picking
   * tab's Material / Diamond sections are stale afterwards — the viewer emits
   * no event for it, and `material` now has a seed, so refreshing here is what
   * makes the inspector show the variant's actual values.
   */
  const apply = async (name: string) => {
    setBusy(name);
    setError(null);
    try {
      await r.applyMaterialVariant(name);
      await Promise.all([store.refresh("material"), store.refresh("groups")]);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(null);
    }
  };

  if (variants.length === 0) {
    return <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">No KHR material variants in this model.</span>;
  }

  return (
    <div className="flex flex-col gap-1 px-3 py-1.5">
      <div className="flex flex-wrap gap-2">
        {variants.map((name) => (
          <MiniBtn
            key={name}
            busy={busy === name}
            // Variants all rewrite the same materials: a second click mid-swap
            // would race the first and leave a half-applied mix.
            disabled={busy !== null}
            onClick={() => void apply(name)}
          >
            {name}
          </MiniBtn>
        ))}
      </div>
      {error ? <span className="text-[11px] text-ed-red">{error}</span> : null}
    </div>
  );
}
